import {
  Alert,
  Button,
  Card,
  Drawer,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useCallback } from 'react'

import { bookingLabels, typeLabels } from '~/features/lessons/model'
import { useRemoteData } from '~/features/performance/useRemoteData'
import {
  type SlotTeacher,
  type StudentFixedSlot,
  type StudentSingleSlot,
  occurrenceState,
} from '~/features/student-slots/model'
import { getStudentOccupiedSlots } from '~/services/student'
import type { IStudent } from '~/types/student'

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
const format = (value: number) => dayjs(value).format('YYYY-MM-DD HH:mm')
const teacher = (value: SlotTeacher) => (
  <Space direction="vertical" size={0}>
    <span>{value.name ?? '老师信息不可用'}</span>
    <Typography.Text type="secondary">ID: {value.id}</Typography.Text>
    {value.onDuty === false && <Tag>已下岗</Tag>}
  </Space>
)
const weeklyTime = (slot: StudentFixedSlot) => {
  const start = dayjs(slot.nextStart)
  const end = slot.nextEnd == null ? null : dayjs(slot.nextEnd)
  return `每周${'日一二三四五六'[start.day()]} ${start.format('HH:mm')}${end ? `–${end.isSame(start, 'day') ? '' : '次日 '}${end.format('HH:mm')}` : ''}`
}

export default function OccupiedSlotsDrawer({
  student,
  onClose,
}: {
  student: IStudent
  onClose: () => void
}) {
  const request = useCallback(
    () => getStudentOccupiedSlots(student.id),
    [student.id],
  )
  const { data, loading, error, retry } = useRemoteData(request)
  const fixedColumns: ColumnsType<StudentFixedSlot> = [
    { title: '每周占用时段', width: 190, render: (_, row) => weeklyTime(row) },
    { title: '老师', dataIndex: 'teacher', width: 150, render: teacher },
    { title: '下个时间点', dataIndex: 'nextStart', width: 165, render: format },
    {
      title: '下个时点的预约',
      width: 290,
      render: (_, row) => {
        const state = occurrenceState(row)
        return (
          <Space direction="vertical" size={4}>
            <span>
              <Tag color={state.color}>{state.label}</Tag>
              {row.nextAppointment &&
                (bookingLabels[row.nextAppointment.status] ??
                  row.nextAppointment.status)}
            </span>
            {state.description && (
              <Typography.Text type="secondary">
                {state.description}
              </Typography.Text>
            )}
            {row.nextAppointment && (
              <Typography.Text type="secondary">
                预约 ID: {row.nextAppointment.id}
              </Typography.Text>
            )}
            {row.nextAppointment?.cancelReason && (
              <span>{row.nextAppointment.cancelReason}</span>
            )}
          </Space>
        )
      },
    },
    {
      title: '占用依据',
      width: 180,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>固定绑定 #{row.bindingId}</span>
          <Typography.Text type="secondary">
            时段 #{row.fixedSlotId}
          </Typography.Text>
        </Space>
      ),
    },
  ]
  const singleColumns: ColumnsType<StudentSingleSlot> = [
    {
      title: '占用时间',
      width: 190,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{format(row.start)}</span>
          {row.end != null && (
            <Typography.Text type="secondary">
              至 {format(row.end)}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    { title: '老师', dataIndex: 'teacher', width: 150, render: teacher },
    {
      title: '类型',
      dataIndex: 'type',
      width: 180,
      render: (value) =>
        value === 'FIXED'
          ? '固定课（无有效绑定）'
          : (typeLabels[value] ?? value),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 200,
      render: (value) => bookingLabels[value] ?? value,
    },
    {
      title: '占用依据',
      width: 190,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>预约 #{row.appointmentId}</span>
          {row.scheduleDetailId && (
            <Typography.Text type="secondary">
              单次时段 #{row.scheduleDetailId}
            </Typography.Text>
          )}
        </Space>
      ),
    },
  ]
  return (
    <Drawer
      title={`占用时段 · ${student.name}`}
      open
      onClose={onClose}
      width="min(1100px, 96vw)"
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ display: 'flex' }}>
        <Alert
          type="info"
          showIcon
          message="固定时段来自学生的每周绑定，即使下次预约尚未生成也会显示。"
          description={`以下时间按 ${timezone} 显示。“尚未生成”仅表示没有对应预约记录，可结合老师状态和续约条件进一步核对。`}
        />
        <Button
          onClick={retry}
          loading={loading}
          style={{ alignSelf: 'flex-start' }}
        >
          刷新占用时段
        </Button>
        {loading ? (
          <Spin />
        ) : error || !data ? (
          <Alert type="error" showIcon message="占用时段加载失败，请重试" />
        ) : (
          <>
            <Card
              title={`每周固定占用 · ${data.fixedSlots.length} 个时段`}
              size="small"
            >
              <Table<StudentFixedSlot>
                rowKey="bindingId"
                size="small"
                columns={fixedColumns}
                dataSource={data.fixedSlots}
                pagination={false}
                scroll={{ x: 975 }}
                locale={{ emptyText: '暂无有效的每周固定绑定' }}
              />
            </Card>
            <Card
              title={`单次 / 独立占用 · ${data.singleSlots.length} 个时段`}
              size="small"
            >
              <Typography.Paragraph type="secondary">
                显示未来有效的单次占用，以及没有对应固定绑定的独立课次；已取消、已过期的记录不占用时段。
              </Typography.Paragraph>
              <Table<StudentSingleSlot>
                rowKey="appointmentId"
                size="small"
                columns={singleColumns}
                dataSource={data.singleSlots}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                scroll={{ x: 910 }}
                locale={{ emptyText: '暂无未来单次占用' }}
              />
            </Card>
            <Card
              title={`待老师接受的申请 · ${data.pendingRequests.length} 个`}
              size="small"
            >
              <Typography.Paragraph type="secondary">
                这些时间仍在申请中，会临时占用对应日期，尚未形成成功预约或每周固定绑定。
              </Typography.Paragraph>
              <Table<StudentSingleSlot>
                rowKey="appointmentId"
                size="small"
                columns={[
                  ...singleColumns.slice(0, 4),
                  {
                    title: '接受截止',
                    dataIndex: 'desiredExpiresAt',
                    width: 180,
                    render: (value) => (value == null ? '—' : format(value)),
                  },
                ]}
                dataSource={data.pendingRequests}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                scroll={{ x: 900 }}
                locale={{ emptyText: '暂无待接受申请' }}
              />
            </Card>
            <Typography.Text type="secondary">
              数据更新于 {format(data.asOf)}
              ；固定时段的具体日期会随每周循环推进。
            </Typography.Text>
          </>
        )}
      </Space>
    </Drawer>
  )
}
